import { useState, useEffect, useContext } from 'react'
import { AuthContext } from '../contexts/AuthContext'
import './Preorders.css'

const API = process.env.REACT_APP_API || ''

const apiFetch = async (path, opts = {}) => {
    const res = await fetch(API + path, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
        ...opts,
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
    return data
}

const Preorders = () => {
    const { user } = useContext(AuthContext)
    const brandId = user?.brand

    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [notifying, setNotifying] = useState(null)
    const [editingOrderId, setEditingOrderId] = useState(null)
    const [newDelivery, setNewDelivery] = useState('')
    const [saving, setSaving] = useState(false)

    const loadPreorders = async () => {
        if (!brandId) return
        setLoading(true)
        try {
            const data = await apiFetch(`/brand/${brandId}/orders?status=confirmed`)
            const preorders = (data.orders || []).filter(o => o.isPreorder)
            setOrders(preorders)
        } catch { setOrders([]) }
        finally { setLoading(false) }
    }

    useEffect(() => { loadPreorders() }, [brandId])

    const handleNotify = async (orderId) => {
        setNotifying(orderId)
        try {
            // Déclenche la notif manuellement via le worker
            await apiFetch(`/brand/${brandId}/orders/${orderId}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status: 'shipped' }),
            })
            alert('Notification "bientôt disponible" envoyée.')
        } catch (err) {
            alert(err.message)
        } finally {
            setNotifying(null)
        }
    }

    const openEditDelivery = (order) => {
        setEditingOrderId(order._id)
        setNewDelivery(order.expectedDelivery ? order.expectedDelivery.slice(0, 10) : '')
    }

    const saveDelivery = async (orderId) => {
        setSaving(true)
        try {
            // Note: la mise à jour de expectedDelivery nécessite un endpoint PATCH order
            // Pour l'instant on patch le status avec la date intégrée — à étendre si besoin
            await apiFetch(`/brand/${brandId}/orders/${orderId}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status: 'confirmed' }),
            })
            setOrders(prev => prev.map(o =>
                o._id === orderId ? { ...o, expectedDelivery: newDelivery } : o
            ))
            setEditingOrderId(null)
            alert('Date de livraison mise à jour. Les acheteurs seront notifiés automatiquement par le worker preorder:ship-soon à J-7.')
        } catch (err) {
            alert(err.message)
        } finally {
            setSaving(false)
        }
    }

    if (loading) return <div className="preorders-loading">Chargement...</div>

    return (
        <div className="preorders-page">
            <div className="preorders-header">
                <h2>Précommandes</h2>
                <span className="preorders-count">{orders.length} précommande{orders.length !== 1 ? 's' : ''} en cours</span>
            </div>

            {orders.length === 0 ? (
                <p className="preorders-empty">
                    Aucune précommande confirmée. Créez un produit avec le mode "Précommande" depuis l'Inventaire.
                </p>
            ) : (
                <div className="preorders-list">
                    {orders.map(order => (
                        <div key={order._id} className="preorders-card">
                            <div className="preorders-card-header">
                                <span className="preorders-order-id">#{order._id.slice(-8)}</span>
                                <span className="preorders-amount">{(order.totalAmount / 100).toFixed(2)} €</span>
                            </div>

                            <div className="preorders-items">
                                {order.items.map((item, i) => (
                                    <div key={i} className="preorders-item">
                                        <span className="preorders-item-name">{item.name}</span>
                                        <span className="preorders-item-meta">
                                            × {item.qty} · {Object.values(item.attributes || {}).join(', ')}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="preorders-delivery-row">
                                <span className="preorders-label">Livraison estimée :</span>
                                {editingOrderId === order._id ? (
                                    <div className="preorders-edit-delivery">
                                        <input
                                            type="date"
                                            value={newDelivery}
                                            onChange={e => setNewDelivery(e.target.value)}
                                        />
                                        <button
                                            className="preorders-btn-save"
                                            onClick={() => saveDelivery(order._id)}
                                            disabled={saving}
                                        >
                                            {saving ? '...' : '✓'}
                                        </button>
                                        <button className="preorders-btn-cancel" onClick={() => setEditingOrderId(null)}>✕</button>
                                    </div>
                                ) : (
                                    <>
                                        <span className="preorders-delivery-date">
                                            {order.expectedDelivery
                                                ? new Date(order.expectedDelivery).toLocaleDateString('fr-FR')
                                                : 'Non définie'}
                                        </span>
                                        <button className="preorders-btn-edit" onClick={() => openEditDelivery(order)}>
                                            Modifier
                                        </button>
                                    </>
                                )}
                            </div>

                            <div className="preorders-card-footer">
                                <span className="preorders-address">
                                    {order.shippingAddress?.city}, {order.shippingAddress?.country}
                                </span>
                                <span className="preorders-date">
                                    {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default Preorders
